'use client';

import { clsx } from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { stripLocalePrefix } from '~/i18n/i18n-config';
import { usePartner } from '~/libs/partners';
import { useJourneyStore } from '~/stores/journeyStore';
import { FIRST_CLASS_PROMO_BANNER } from '~/utils/promoHelper';
import styles from './GlobalBanner.module.css';

type BannerDriver = () => { isLive: boolean; url: string };

type GlobalBannerProps = {
  driver?: BannerDriver;
};

const INVALID_ROUTES_FOR_BANNER = ['/checkout', '/account', '/confirmation', '/journey-guide', '/map'];
const GlobalBanner = ({ driver = FIRST_CLASS_PROMO_BANNER }: GlobalBannerProps = {}) => {
  const t = useTranslations('content.promo');
  // Strip the [lang] prefix so route gates hold on both trees (/en-US/checkout/* included).
  const pathname = stripLocalePrefix(usePathname() || '');
  const isInvalidPage = INVALID_ROUTES_FOR_BANNER.some((route) => pathname.startsWith(route));
  const partner = usePartner();
  const allowItineraryEdits = useJourneyStore((state) => state.journey?.allowItineraryEdits);
  const { isLive, url } = driver();

  const isNonEditableJourney = pathname.startsWith('/journey/') && allowItineraryEdits !== true;

  // Preview-only override: ?previewBanner=1 forces the banner visible for stakeholder
  // sign-off before the live window opens. It relaxes the DATE gate ONLY — route
  // exclusions, the non-editable-journey gate, and the partner hide below still apply.
  const isPreviewForced = useSearchParams()?.get('previewBanner') === '1';
  const isWithinLiveWindow = isLive || isPreviewForced;

  if (partner || isInvalidPage || !isWithinLiveWindow || isNonEditableJourney) return null;

  return (
    <div
      className={clsx(
        styles.banner,
        'flex flex-col-reverse items-center justify-center p-2 text-sm text-center text-white z-[99] md:text-base md:text-md'
      )}
    >
      <p className="md:w-4/5">
        {t('first_class_copy')}{' '}
        <a
          href={url}
          aria-label={t('first_class_aria')}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {t('first_class_link')}
        </a>
        .
      </p>
    </div>
  );
};

export { GlobalBanner };
